import { getDustValue } from '@exodus/bitcoin-api'
import { t } from '@exodus/i18n-dummy'

import { FIELDS, PRIORITY_LEVELS, VALIDATION_TYPES } from '../constants.js'
import createNotEnoughtOutputValidator from './createNotEnoughtOutputValidator.js'
import createValidator from './createValidator.js'

const bip70Validator = createValidator({
  id: 'BIP70',
  type: VALIDATION_TYPES.ERROR,
  priority: PRIORITY_LEVELS.MIDDLE,
  field: FIELDS.ADDRESS,
  shouldValidate: ({ bip70 }) => !!bip70,
  isValid: async ({ bip70 }) => !bip70.isInvalid(),
  getMessage: () => t(`The payment request is invalid.`),
})

const bitcoinAddressPurposeValidator = createValidator({
  id: 'BTC_ADDRESS_PURPOSE',
  type: VALIDATION_TYPES.WARN,
  priority: PRIORITY_LEVELS.BASE,
  field: FIELDS.ADDRESS,
  shouldValidate: ({ asset }) => asset.assetType === 'BRC20_TOKEN',
  isValid: async ({ asset, destinationAddress }) => {
    const baseAsset = asset.baseAsset

    const isAddressValid = await baseAsset.address.validate(destinationAddress)
    const addressPurpose = await baseAsset.address.resolvePurpose(destinationAddress)
    const isTaprootAddress = addressPurpose === 86

    return isAddressValid && isTaprootAddress
  },
  getMessage: () => t(`Use a Bitcoin Taproot address to send BRC-20 tokens.`),
})

const bcnLegacyAddressValidator = createValidator({
  type: VALIDATION_TYPES.WARN,
  priority: PRIORITY_LEVELS.BASE,
  field: FIELDS.ADDRESS,
  id: 'BCN_LEGACY_ADDRESS',
  shouldValidate: ({ asset }) => asset.ticker === 'BCH',
  isValid: async ({ asset, destinationAddress }) => {
    return (
      !destinationAddress ||
      (asset.address.validate(destinationAddress) &&
        !asset.address.isLegacyAddress(destinationAddress))
    )
  },
  getMessage: () =>
    t(
      'Are you sure you want to send Bitcoin Cash (BCH) using an old address format? Did you check to make sure you are not sending to a Bitcoin (BTC) address?\n\nMixing BCH and BTC addresses can result in a loss of funds.'
    ),
})

const notEnoughOutputValidator = createNotEnoughtOutputValidator({
  getMinAmount: ({ asset }) => getDustValue(asset),
  getMessage: ({ asset, getFiatValue, formatPrice }) => {
    const dust = getDustValue(asset)
    const dustValue =
      formatPrice(getFiatValue(dust)) + ' (' + dust.toBaseString({ unit: true }) + ')'

    return t(`Sending ${dustValue} or less isn't supported on the ${asset.displayName} network`)
  },
})

const bitcoinCpfpWarning = createValidator({
  id: 'BITCOIN_CPFP_WARNING',
  type: VALIDATION_TYPES.WARN,
  shouldValidate: ({ fees, getFiatValue, formatPrice }) => fees && getFiatValue && formatPrice,
  isValid: ({ fees }) => {
    const type = fees?.extraFeeData?.type
    return (
      !fees?.extraFeeData?.extraFee ||
      fees?.extraFeeData?.extraFee?.isZero ||
      (type !== 'cpfp' && type !== 'cpfpdust')
    )
  },
  getMessage: ({ formatPrice, getFiatValue, fees }) => {
    const extraFee = fees.extraFeeData.extraFee
    const extraFeeDisplayValue = formatPrice(getFiatValue(extraFee))
    return t(
      `You're paying ${extraFeeDisplayValue} in extra fees for spending unconfirmed transactions (CPFP).`
    )
  },
})

export default [
  bitcoinCpfpWarning,
  bip70Validator,
  bcnLegacyAddressValidator,
  bitcoinAddressPurposeValidator,
  notEnoughOutputValidator,
]
